import React from "react";
import { motion } from "framer-motion";
import { THEMES } from "./themes";

// ───────────────────────────────────────────────────────────────────
// Wax Seal — pressed-into-paper stamp, only shown when invoice paid.
// Per-theme inner motif, mirrors PDF for visual continuity.
// ───────────────────────────────────────────────────────────────────
export const WaxSeal = ({ theme = "mono", brand = "#111111" }) => {
  const t = THEMES[theme] || THEMES.mono;
  const ring = t.sealRing;
  const ink = t.sealInk;
  const fontSerif = "Georgia, serif";
  const fontSans = "Helvetica, Arial, sans-serif";

  const motifs = {
    mono: (
      <>
        <circle cx="60" cy="60" r="14" fill="none" stroke={ring} strokeWidth="1.2" />
        <path d="M53 60 L60 53 L67 60 L60 67 Z" fill={ink} />
        <text x="60" y="38" textAnchor="middle" fontSize="7" letterSpacing="2.2" fill={ink} fontFamily={fontSans}>
          PAID · QUILL
        </text>
        <text x="60" y="92" textAnchor="middle" fontSize="7" letterSpacing="2.2" fill={ink} fontFamily={fontSans}>
          SETTLED
        </text>
      </>
    ),
    atelier: (
      <>
        <path d="M60 44 L72 60 L60 76 L48 60 Z" fill="none" stroke={ring} strokeWidth="1.4" />
        <circle cx="60" cy="60" r="3.5" fill={ink} />
        <text x="60" y="36" textAnchor="middle" fontSize="6.5" letterSpacing="3" fill={ink} fontFamily={fontSerif}>
          ATELIER
        </text>
        <text x="60" y="92" textAnchor="middle" fontSize="6.5" letterSpacing="3" fill={ink} fontFamily={fontSerif}>
          CONFIRMED
        </text>
      </>
    ),
    manuscript: (
      <>
        <path
          d="M50 50 Q60 40 70 50 Q80 60 70 70 Q60 80 50 70 Q40 60 50 50 Z"
          fill="none"
          stroke={ring}
          strokeWidth="1"
        />
        <text x="60" y="65" textAnchor="middle" fontSize="16" fontStyle="italic" fill={ink} fontFamily={fontSerif}>
          P
        </text>
        <text x="60" y="36" textAnchor="middle" fontSize="6.5" letterSpacing="2.5" fill={ink} fontFamily={fontSerif}>
          SIGILLUM
        </text>
        <text x="60" y="92" textAnchor="middle" fontSize="6.5" letterSpacing="2.5" fill={ink} fontFamily={fontSerif}>
          QUILL · MMXXVI
        </text>
      </>
    ),
    modernist: (
      <>
        <rect x="46" y="46" width="28" height="28" fill="none" stroke={ring} strokeWidth="1.5" />
        <line x1="60" y1="46" x2="60" y2="74" stroke={ring} strokeWidth="1.5" />
        <line x1="46" y1="60" x2="74" y2="60" stroke={ring} strokeWidth="1.5" />
        <text x="60" y="36" textAnchor="middle" fontSize="7" letterSpacing="3" fill={ink} fontFamily={fontSans}>
          SETTLED ◼ PAID
        </text>
        <text x="60" y="92" textAnchor="middle" fontSize="7" letterSpacing="3" fill={ink} fontFamily={fontSans}>
          QUILL · LEDGER
        </text>
      </>
    ),
  };
  const inner = motifs[theme] || motifs.mono;
  const gradId = `wax-${theme}`;

  return (
    <motion.svg
      initial={{ scale: 0.6, rotate: -18, opacity: 0 }}
      animate={{ scale: 1, rotate: -8, opacity: 1 }}
      transition={{ type: "spring", stiffness: 120, damping: 14, delay: 0.15 }}
      viewBox="0 0 120 120"
      width="128"
      height="128"
      data-testid="wax-seal"
      style={{ filter: "drop-shadow(0 6px 12px rgba(0,0,0,0.45))" }}
    >
      <defs>
        <radialGradient id={gradId} cx="40%" cy="38%" r="65%">
          <stop offset="0%" stopColor={brand} stopOpacity="0.96" />
          <stop offset="60%" stopColor={brand} stopOpacity="0.9" />
          <stop offset="100%" stopColor="#000" stopOpacity="0.78" />
        </radialGradient>
      </defs>
      <circle cx="60" cy="60" r="52" fill={`url(#${gradId})`} />
      <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.5" />
      <circle cx="60" cy="60" r="46" fill="none" stroke="rgba(255,255,255,0.22)" strokeWidth="0.8" />
      {inner}
    </motion.svg>
  );
};
